import { Box, Tab, Tabs } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { IdCard } from 'lucide-react';
import { DrawerCloseControl, useHasDrawerControl } from 'src/components/Navbar/DesktopDrawer';
import { PageContainer, PageHeader } from 'src/components/Page';
import { useUrlParamTab } from 'src/utils/hooks';
import { CgCustomDomainPanel } from './custom-domain/cg-custom-domain-panel';
import { useMembershipsPermissions } from './hooks/use-memberships-permissions';
import { MembershipsConfigView } from './memberships-config-view';

enum MembershipsTab {
  Pages = 'pages',
  Domain = 'domain',
}

export const MembershipsView = () => {
  const { canManageLandingPageDomains } = useMembershipsPermissions();
  const hasDrawerControl = useHasDrawerControl();

  const tabItems = [
    { value: MembershipsTab.Pages, label: 'Pages' },
    ...(canManageLandingPageDomains
      ? [{ value: MembershipsTab.Domain, label: 'Custom domain' }]
      : []),
  ];

  const { activeTab, setActiveTab } = useUrlParamTab<MembershipsTab>({
    paramName: 'tab',
    validValues: tabItems.map((tab) => tab.value),
    defaultValue: MembershipsTab.Pages,
  });

  const isDomainTab = activeTab === MembershipsTab.Domain;

  return (
    <V2ThemeProvider>
      <Box
        sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'custom.OV.sys.outlineVariant' }}>
          <PageContainer.Fluid>
            <PageHeader
              startAdornment={hasDrawerControl ? <DrawerCloseControl /> : undefined}
              icon={<IdCard />}
              title="Memberships"
              subtitle="Build the plans and pages clients sign up from, and see the address those pages are served on."
            />
            {tabItems.length > 1 ? (
              <Tabs
                value={activeTab}
                onChange={(_event, value: MembershipsTab) => setActiveTab(value)}
              >
                {tabItems.map((item) => (
                  <Tab key={item.value} value={item.value} label={item.label} />
                ))}
              </Tabs>
            ) : null}
          </PageContainer.Fluid>
        </Box>

        {/* Kept mounted so switching tabs doesn't drop unsaved builder changes. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: isDomainTab ? 'none' : 'flex',
            flexDirection: 'column',
          }}
        >
          <MembershipsConfigView />
        </Box>

        {isDomainTab ? (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <PageContainer.Fluid sx={{ pt: 3, pb: 6 }}>
              <CgCustomDomainPanel />
            </PageContainer.Fluid>
          </Box>
        ) : null}
      </Box>
    </V2ThemeProvider>
  );
};
